import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { 
  CheckCircle, Circle, Clock, AlertCircle, User, Calendar,
  ChevronRight, FileCheck, Stethoscope, Pill, Activity,
  Flag, ArrowRight, MoreVertical, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MILESTONES = [
  { id: 'screening', name: 'Screening', icon: User, description: 'Initial eligibility assessment' },
  { id: 'consent', name: 'Consent', icon: FileCheck, description: 'Informed consent obtained' },
  { id: 'baseline', name: 'Baseline', icon: Stethoscope, description: 'Baseline assessments complete' },
  { id: 'treatment', name: 'Treatment', icon: Pill, description: 'Active treatment phase' },
  { id: 'followup', name: 'Follow-up', icon: Activity, description: 'Post-treatment monitoring' },
  { id: 'completion', name: 'Completion', icon: Flag, description: 'Study completed' },
];

const SubjectProgressTracking = () => {
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [viewMode, setViewMode] = useState('list');
  const [loading, setLoading] = useState(true);
  const [subjectsWithProgress, setSubjectsWithProgress] = useState([]);

  // Fetch all subjects with progress
  const fetchProgress = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/registry/progress/all`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        const transformed = data.subjects.map(item => ({
          id: item.subject.rid,
          ssid: item.subject.ssid,
          name: `Subject ${item.subject.ssid.split('-').pop()}`,
          study: item.subject.condition,
          currentMilestone: item.progress.current_milestone,
          milestones: item.progress.milestones,
          progress: item.progress.progress_percent
        }));
        setSubjectsWithProgress(transformed);
      }
    } catch (err) {
      console.error('Failed to fetch progress:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProgress();
  }, [fetchProgress]);

  const updateMilestone = async (subjectId, milestoneId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/registry/subjects/${subjectId}/progress`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          milestone_id: milestoneId,
          status: newStatus,
          notes: `Status updated to ${newStatus}`
        })
      });
      
      if (response.ok) {
        toast.success('Progress updated');
        fetchProgress();
      } else {
        toast.error('Failed to update progress');
      }
    } catch (err) {
      toast.error('Failed to update progress');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'in_progress':
        return <Clock className="w-5 h-5 text-blue-500 animate-pulse" />;
      case 'overdue':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Circle className="w-5 h-5 text-gray-300" />;
    }
  };

  const getProgressColor = (progress) => {
    if (progress >= 80) return 'bg-green-500';
    if (progress >= 50) return 'bg-blue-500';
    if (progress >= 25) return 'bg-orange-500';
    return 'bg-gray-400';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#1877F2]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <User className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-700">{subjectsWithProgress.length}</p>
                <p className="text-xs text-blue-600">Active Subjects</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm bg-orange-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-orange-700">
                  {subjectsWithProgress.filter(s => s.milestones[s.currentMilestone]?.status === 'in_progress').length}
                </p>
                <p className="text-xs text-orange-600">In Progress</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm bg-green-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-700">
                  {subjectsWithProgress.filter(s => s.progress === 100).length}
                </p>
                <p className="text-xs text-green-600">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm bg-purple-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Activity className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-700">
                  {subjectsWithProgress.length > 0 ? Math.round(subjectsWithProgress.reduce((acc, s) => acc + s.progress, 0) / subjectsWithProgress.length) : 0}%
                </p>
                <p className="text-xs text-purple-600">Avg Progress</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* View Toggle */}
      <div className="flex justify-end">
        <div className="bg-gray-100 rounded-lg p-1 flex gap-1">
          <button
            onClick={() => setViewMode('list')}
            className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
              viewMode === 'list' ? 'bg-white shadow text-gray-900' : 'text-gray-600'
            }`}
          >
            List View
          </button>
          <button
            onClick={() => setViewMode('timeline')}
            className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
              viewMode === 'timeline' ? 'bg-white shadow text-gray-900' : 'text-gray-600'
            }`}
          >
            Timeline View
          </button>
        </div>
      </div>

      {/* Subject List */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium text-gray-700 flex items-center">
            <Activity className="w-5 h-5 mr-2 text-[#1877F2]" />
            Subject Progress Tracking
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {subjectsWithProgress.map((subject) => (
              <div 
                key={subject.id}
                className={`p-4 rounded-lg border transition-all cursor-pointer ${
                  selectedSubject?.id === subject.id 
                    ? 'border-[#1877F2] bg-blue-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setSelectedSubject(selectedSubject?.id === subject.id ? null : subject)}
              >
                {/* Subject Header */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center font-bold text-gray-600">
                      {subject.name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{subject.name}</p>
                      <p className="text-xs text-gray-500">{subject.ssid} • {subject.study}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-sm font-bold text-gray-900">{subject.progress}%</p>
                      <p className="text-xs text-gray-500">Complete</p>
                    </div>
                    <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${
                      selectedSubject?.id === subject.id ? 'rotate-90' : ''
                    }`} />
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                  <div 
                    className={`h-2 rounded-full transition-all ${getProgressColor(subject.progress)}`}
                    style={{ width: `${subject.progress}%` }}
                  ></div>
                </div>

                {/* Milestone Icons Row */}
                <div className="flex items-center justify-between">
                  {MILESTONES.map((milestone, idx) => {
                    const status = subject.milestones[milestone.id]?.status;
                    const MilestoneIcon = milestone.icon;
                    return (
                      <div key={milestone.id} className="flex items-center">
                        <div className={`flex flex-col items-center ${
                          status === 'completed' ? 'text-green-600' :
                          status === 'in_progress' ? 'text-blue-600' :
                          'text-gray-300'
                        }`}>
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            status === 'completed' ? 'bg-green-100' :
                            status === 'in_progress' ? 'bg-blue-100 ring-2 ring-blue-300' :
                            'bg-gray-100'
                          }`}>
                            <MilestoneIcon className="w-4 h-4" />
                          </div>
                          <span className="text-[10px] mt-1">{milestone.name}</span>
                        </div>
                        {idx < MILESTONES.length - 1 && (
                          <div className={`w-8 h-0.5 mx-1 ${
                            subject.milestones[MILESTONES[idx + 1].id]?.status !== 'pending' 
                              ? 'bg-green-400' 
                              : 'bg-gray-200'
                          }`}></div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Expanded Timeline */}
                {selectedSubject?.id === subject.id && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Milestone Timeline</h4>
                    <div className="relative">
                      <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                      {MILESTONES.map((milestone) => {
                        const data = subject.milestones[milestone.id];
                        const MilestoneIcon = milestone.icon;
                        return (
                          <div key={milestone.id} className="flex items-start gap-4 mb-4 relative">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center z-10 ${
                              data.status === 'completed' ? 'bg-green-100' :
                              data.status === 'in_progress' ? 'bg-blue-100' :
                              'bg-gray-100'
                            }`}>
                              {data.status === 'completed' ? (
                                <CheckCircle className="w-4 h-4 text-green-600" />
                              ) : data.status === 'in_progress' ? (
                                <Clock className="w-4 h-4 text-blue-600" />
                              ) : (
                                <Circle className="w-4 h-4 text-gray-400" />
                              )}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <p className={`font-medium ${
                                  data.status === 'pending' ? 'text-gray-400' : 'text-gray-900'
                                }`}>{milestone.name}</p>
                                {data.date && (
                                  <span className="text-xs text-gray-500">{data.date}</span>
                                )}
                              </div>
                              <p className="text-xs text-gray-500">{milestone.description}</p>
                              {data.notes && (
                                <p className="text-xs text-blue-600 mt-1 bg-blue-50 px-2 py-1 rounded inline-block">
                                  {data.notes}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubjectProgressTracking;
